import { detectFaceWithMediapipe } from './mediapipeFaceDetection';

export type FaceDetectionBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type FaceDetectorInstance = {
  detect: (image: ImageBitmapSource) => Promise<Array<{ boundingBox?: FaceDetectionBox | DOMRectReadOnly }>>;
};

/**
 * Normalisiert eine Bounding Box zu einem einheitlichen Format
 */
function normalizeBoundingBox(box: FaceDetectionBox | DOMRectReadOnly | undefined | null): FaceDetectionBox | null {
  if (!box) {
    return null;
  }

  const x = 'x' in box ? box.x : ('left' in (box as DOMRectReadOnly) ? (box as DOMRectReadOnly).left : 0);
  const y = 'y' in box ? box.y : ('top' in (box as DOMRectReadOnly) ? (box as DOMRectReadOnly).top : 0);
  const width = 'width' in box ? box.width : 0;
  const height = 'height' in box ? box.height : 0;

  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
    return null;
  }

  return { x, y, width, height };
}

/**
 * Fallback-Gesichtserkennung: Verwendet das Zentrum des Bildes
 */
function runFallbackFaceDetection(img: HTMLImageElement | HTMLCanvasElement): FaceDetectionBox | null {
  const centerX = img.width / 2;
  const centerY = img.height / 2;
  
  // Gesichtsgröße basierend auf der kleineren Bildseite
  const faceSize = Math.min(img.width, img.height) * 0.6; // 60% der kleineren Seite
  
  const result = {
    x: Math.max(0, centerX - faceSize / 2),
    y: Math.max(0, centerY - faceSize / 2),
    width: Math.min(faceSize, img.width),
    height: Math.min(faceSize, img.height),
  };
  
  console.log('[BiometricCentering] Fallback - Zentrum verwendet:', {
    imageSize: { width: img.width, height: img.height },
    faceBounds: result,
    faceSize: faceSize
  });
  
  return result;
}

/**
 * Führt Gesichtserkennung durch (FaceDetector API, Mediapipe oder Fallback)
 */
async function runFaceDetection(
  image: HTMLImageElement | HTMLCanvasElement,
  faceDetector: FaceDetectorInstance | null
): Promise<FaceDetectionBox | null> {
  // Versuche FaceDetector API
  if (faceDetector) {
    try {
      console.log('[BiometricCentering] Nutze FaceDetector API');
      const results = await faceDetector.detect(image);
      if (Array.isArray(results) && results.length > 0) {
        const normalized = normalizeBoundingBox(results[0].boundingBox);
        if (normalized && normalized.width > 10 && normalized.height > 10) {
          console.log('[BiometricCentering] FaceDetector API erfolgreich');
          return normalized;
        }
      }
    } catch (error) {
      console.warn('[BiometricCentering] FaceDetector API nicht verfügbar:', error);
    }
  }

  // Versuche Mediapipe
  try {
    console.log('[BiometricCentering] Versuche Mediapipe FaceDetection');
    const mediapipeBounds = await detectFaceWithMediapipe(image);
    if (mediapipeBounds && mediapipeBounds.width > 10 && mediapipeBounds.height > 10) {
      console.log('[BiometricCentering] Mediapipe-Erkennung erfolgreich');
      return mediapipeBounds;
    }
  } catch (mediapipeError) {
    console.warn('[BiometricCentering] Mediapipe-Erkennung fehlgeschlagen', mediapipeError);
  }

  // Fallback: Zentrum verwenden
  console.log('[BiometricCentering] Starte Fallback-Erkennung');
  return runFallbackFaceDetection(image);
}

/**
 * Führt biometrische Zentrierung eines Bildes durch
 * Erkennt das Gesicht und zentriert es mit angemessenem Padding
 */
export async function applyBiometricCentering(
  imageSource: HTMLImageElement | HTMLCanvasElement | string | Blob
): Promise<Blob> {
  console.log('[BiometricCentering] 🚀 Starte biometrische Zentrierung...', {
    inputType: imageSource instanceof Blob ? 'Blob' : 
               imageSource instanceof HTMLImageElement ? 'HTMLImageElement' :
               imageSource instanceof HTMLCanvasElement ? 'HTMLCanvasElement' : 
               typeof imageSource === 'string' ? 'string' : 'unknown',
    inputSize: imageSource instanceof Blob ? imageSource.size : 'N/A'
  });
  
  // Konvertiere verschiedene Input-Typen zu einem Image-Element
  let img: HTMLImageElement;
  
  if (typeof imageSource === 'string') {
    // Data URL oder URL
    img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = imageSource;
    });
  } else if (imageSource instanceof Blob) {
    // Blob
    console.log('[BiometricCentering] Lade Bild von Blob, Größe:', imageSource.size);
    const url = URL.createObjectURL(imageSource);
    img = new Image();
    // Kein crossOrigin für Blob-URLs nötig
    await new Promise<void>((resolve, reject) => {
      img.onload = () => {
        console.log('[BiometricCentering] Bild von Blob geladen:', {
          width: img.width,
          height: img.height,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight
        });
        URL.revokeObjectURL(url);
        resolve();
      };
      img.onerror = (error) => {
        console.error('[BiometricCentering] Fehler beim Laden des Bildes von Blob:', error);
        URL.revokeObjectURL(url);
        reject(new Error('Fehler beim Laden des Bildes'));
      };
      img.src = url;
    });
    
    // Warte auf vollständiges Laden
    if (img.width === 0 || img.height === 0) {
      console.error('[BiometricCentering] Bild hat ungültige Dimensionen:', img.width, 'x', img.height);
      throw new Error('Bild hat ungültige Dimensionen');
    }
  } else if (imageSource instanceof HTMLCanvasElement) {
    // Canvas - konvertiere zu Image
    const dataUrl = imageSource.toDataURL('image/jpeg', 0.95);
    img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = dataUrl;
    });
  } else {
    // Bereits ein Image-Element
    img = imageSource;
  }

  // Initialisiere FaceDetector falls verfügbar
  let faceDetector: FaceDetectorInstance | null = null;
  if (typeof window !== 'undefined' && 'FaceDetector' in window) {
    try {
      faceDetector = new (window as any).FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
    } catch (error) {
      console.warn('[BiometricCentering] FaceDetector konnte nicht initialisiert werden:', error);
    }
  }

  console.log('[BiometricCentering] Bild geladen:', {
    width: img.width,
    height: img.height,
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight
  });

  // Führe Gesichtserkennung durch
  const faceBounds = await runFaceDetection(img, faceDetector);

  console.log('[BiometricCentering] Gesichtserkennung Ergebnis:', faceBounds);

  if (!faceBounds) {
    console.warn('[BiometricCentering] Kein Gesicht erkannt, verwende Originalbild');
    // Wenn kein Gesicht erkannt wurde, gib das Original zurück
    if (imageSource instanceof Blob) {
      return imageSource;
    }
    // Konvertiere zu Blob
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas-Kontext nicht verfügbar');
    }
    ctx.drawImage(img, 0, 0);
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Fehler beim Konvertieren zu Blob'));
        }
      }, 'image/jpeg', 0.95);
    });
  }

  // Berechne Crop-Region basierend auf Gesichtsposition
  // Verwende die gleiche Logik wie in FotoOptimierung
  const padding = 0.4; // 40% Padding um das Gesicht
  const faceSize = Math.max(faceBounds.width, faceBounds.height);
  let cropSize = faceSize * (1 + padding * 2);
  const maxSquare = Math.min(img.width, img.height);
  cropSize = Math.min(Math.max(cropSize, faceSize), maxSquare);

  const faceCenterX = faceBounds.x + faceBounds.width / 2;
  const faceCenterY = faceBounds.y + faceBounds.height / 2;

  let cropX = faceCenterX - cropSize / 2;
  let cropY = faceCenterY - cropSize / 2;

  // Stelle sicher, dass der Crop innerhalb des Bildes bleibt
  cropX = Math.max(0, Math.min(img.width - cropSize, cropX));
  cropY = Math.max(0, Math.min(img.height - cropSize, cropY));

  console.log('[BiometricCentering] Crop-Berechnung:', {
    originalSize: { width: img.width, height: img.height },
    faceBounds,
    faceSize,
    cropSize,
    cropPosition: { x: cropX, y: cropY },
    faceCenter: { x: faceCenterX, y: faceCenterY }
  });

  // Erstelle ein quadratisches Canvas für das zentrierte Bild
  const outputSize = 800; // Standardgröße für Schülerfotos
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Canvas-Kontext nicht verfügbar');
  }

  // Fülle den Hintergrund mit weiß (falls der Crop kleiner ist als das Zielformat)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, outputSize, outputSize);

  // Zeichne den zugeschnittenen Bereich auf das Canvas
  ctx.drawImage(
    img,
    cropX,
    cropY,
    cropSize,
    cropSize,
    0,
    0,
    outputSize,
    outputSize
  );

  console.log('[BiometricCentering] Canvas erstellt:', {
    canvasSize: { width: canvas.width, height: canvas.height },
    sourceCrop: { x: cropX, y: cropY, size: cropSize },
    targetSize: outputSize
  });

  // Konvertiere zu Blob
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        console.log('[BiometricCentering] ✅ Biometrische Zentrierung erfolgreich abgeschlossen', {
          blobSize: blob.size,
          outputSize: outputSize
        });
        resolve(blob);
      } else {
        console.error('[BiometricCentering] ❌ Fehler beim Konvertieren zu Blob');
        reject(new Error('Fehler beim Konvertieren zu Blob'));
      }
    }, 'image/jpeg', 0.95);
  });
}

